// src/components/VisionAssistScreenShare.jsx
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { getAuth } from "firebase/auth";

// ✅ Use the same backend URL resolver your app already uses elsewhere
import { getVisionFrameUrl } from "../utils/visionAssistApi";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const baseUrl = HOST ? `${HOST}` : "";

// ---------- Auth helpers ----------
async function getIdToken() {
  try {
    const user = getAuth()?.currentUser;
    if (!user) return "";
    return await user.getIdToken();
  } catch {
    return "";
  }
}

async function authedUploadForm(path, formData, extra = {}) {
  const overrideToken = extra?.idToken;
  const token = overrideToken || (await getIdToken());
  if (!token) throw new Error("Not authenticated.");

  const headers = { Authorization: `Bearer ${token}` };
  const url = baseUrl ? `${baseUrl}${path}` : path;

  const res = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }

  if (!res.ok || data?.ok === false || data?.success === false) {
    let reason = res.statusText || `HTTP ${res.status}`;
    const errPayload = data?.error;

    if (typeof errPayload === "string") reason = errPayload;
    else if (errPayload && typeof errPayload === "object") {
      reason = errPayload.reason || errPayload.code || data?.reason || reason;
    } else if (typeof data?.reason === "string") reason = data.reason;
    else if (typeof data?.message === "string") reason = data.message;

    const err = new Error(typeof reason === "string" ? reason : "Upload failed");
    err.response = { status: res.status, data };
    throw err;
  }

  return data;
}

// ---------- Small numeric helpers ----------
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const nowMs = () => Date.now();

function safeJsonStringify(obj, maxBytes = 15000) {
  try {
    const s = JSON.stringify(obj || {});
    if (s.length <= maxBytes) return s;
    return JSON.stringify({ _meta_truncated: true });
  } catch {
    return JSON.stringify({});
  }
}

/**
 * Change detection:
 * Downscale into a tiny buffer and compute mean absolute luminance diff.
 */
function computeFrameDiffScore(prev, next) {
  if (!prev || !next) return 0;
  if (prev.length !== next.length) return 0;

  let sum = 0;
  for (let i = 0; i < prev.length; i += 4) {
    const pr = prev[i],
      pg = prev[i + 1],
      pb = prev[i + 2];
    const nr = next[i],
      ng = next[i + 1],
      nb = next[i + 2];

    const pl = (pr * 2 + pg * 3 + pb) / 6;
    const nl = (nr * 2 + ng * 3 + nb) / 6;
    sum += Math.abs(nl - pl);
  }
  const px = prev.length / 4;
  return px ? sum / px : 0;
}

function getTrackInfo(stream) {
  try {
    const track = stream?.getVideoTracks?.()?.[0];
    const settings = track?.getSettings?.() || {};
    return {
      displaySurface: settings.displaySurface || "",
      width: settings.width || 0,
      height: settings.height || 0,
      frameRate: settings.frameRate || 0,
    };
  } catch {
    return { displaySurface: "", width: 0, height: 0, frameRate: 0 };
  }
}

// ---- Refresh-safe extraction helpers ----
function pickFirstString(...vals) {
  for (const v of vals) {
    const s = typeof v === "string" ? v.trim() : "";
    if (s) return s;
  }
  return "";
}
function pickFirstId(...vals) {
  for (const v of vals) {
    const s = String(v || "").trim();
    if (s) return s;
  }
  return "";
}

// ---------------- Thumbnail localStorage helpers ----------------
const THUMB_PREFIX = "va_thumb_v1";
const THUMB_INDEX_PREFIX = "va_thumb_index_v1";
const THUMB_MAX_ITEMS = 20; // keep last N thumbnails per session
const THUMB_MAX_DATAURL_CHARS = 220000; // ~220KB guard

function thumbKey(sessionId, frameKey) {
  const sid = String(sessionId || "").trim();
  const fid = String(frameKey || "").trim();
  if (!sid || !fid) return "";
  return `${THUMB_PREFIX}:${sid}:${fid}`;
}
function indexKey(sessionId) {
  const sid = String(sessionId || "").trim();
  if (!sid) return "";
  return `${THUMB_INDEX_PREFIX}:${sid}`;
}
function safeReadJson(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
function safeWriteJson(key, obj) {
  try {
    localStorage.setItem(key, JSON.stringify(obj));
    return true;
  } catch {
    return false;
  }
}
function safeRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch {}
}
function readThumbDataUrl(sessionId, frameKey) {
  const k = thumbKey(sessionId, frameKey);
  if (!k) return "";
  const obj = safeReadJson(k);
  const dataUrl = String(obj?.dataUrl || "").trim();
  if (dataUrl.startsWith("data:image/")) return dataUrl;
  return "";
}
function writeThumbDataUrl(sessionId, frameKey, dataUrl) {
  const k = thumbKey(sessionId, frameKey);
  if (!k) return false;

  const d = String(dataUrl || "").trim();
  if (!d.startsWith("data:image/")) return false;
  if (d.length > THUMB_MAX_DATAURL_CHARS) return false;

  const ok = safeWriteJson(k, { dataUrl: d, saved_at_ms: nowMs() });
  if (!ok) return false;

  const ik = indexKey(sessionId);
  if (!ik) return true;

  const idx = safeReadJson(ik);
  const list = Array.isArray(idx?.items) ? idx.items : [];
  const t = nowMs();

  const next = [
    { frameKey: String(frameKey || ""), saved_at_ms: t },
    ...list
      .filter((x) => String(x?.frameKey || "") !== String(frameKey || ""))
      .slice(0, THUMB_MAX_ITEMS - 1),
  ];

  safeWriteJson(ik, { items: next });

  const oldBeyond = list.slice(THUMB_MAX_ITEMS - 1);
  oldBeyond.forEach((x) => {
    const fk = String(x?.frameKey || "");
    if (!fk) return;
    safeRemove(thumbKey(sessionId, fk));
  });

  return true;
}

// ---------------- Frames index localStorage helpers (refresh fallback) ----------------
// IMPORTANT:
// - Persist canonical identifiers only.
// - Never persist signed image URLs here. They are temporary credentials and can
//   expire while the page is idle, which previously produced broken silhouettes
//   after refresh.
// - Parent/session hydration remains authoritative for display URLs.
const FRAMES_CACHE_PREFIX = "va_frames_v1";
const FRAMES_CACHE_MAX = 30;
const FRAMES_CACHE_MAX_WRITE = 30;

function framesCacheKey(sessionId) {
  const sid = String(sessionId || "").trim();
  if (!sid) return "";
  return `${FRAMES_CACHE_PREFIX}:${sid}`;
}
function readFramesCache(sessionId) {
  const k = framesCacheKey(sessionId);
  if (!k) return [];
  const obj = safeReadJson(k);
  const items = Array.isArray(obj?.items) ? obj.items : [];

  return items
    .map((x) => ({
      frame_id: String(x?.frame_id || "").trim(),
      session_doc_path: String(x?.session_doc_path || "").trim(),
      saved_at_ms: Number(x?.saved_at_ms || 0) || 0,
    }))
    .filter((x) => x.frame_id || x.session_doc_path);
}
function writeFramesCache(sessionId, items) {
  const k = framesCacheKey(sessionId);
  if (!k) return false;

  const list = (Array.isArray(items) ? items : [])
    .map((x) => ({
      frame_id: String(x?.frame_id || "").trim(),
      session_doc_path: String(x?.session_doc_path || "").trim(),
      saved_at_ms: Number(x?.saved_at_ms || 0) || nowMs(),
    }))
    .filter((x) => x.frame_id || x.session_doc_path)
    .slice(-FRAMES_CACHE_MAX_WRITE);

  return safeWriteJson(k, { items: list });
}
function upsertFramesCache(sessionId, item) {
  const cur = readFramesCache(sessionId);
  const fid = String(item?.frame_id || "").trim();
  const dp = String(item?.session_doc_path || "").trim();

  const key = fid || dp;
  if (!key) return;

  const next = [
    {
      frame_id: fid,
      session_doc_path: dp,
      saved_at_ms: nowMs(),
    },
    ...cur.filter((x) => (x.frame_id || x.session_doc_path) !== key),
  ].slice(0, FRAMES_CACHE_MAX);

  writeFramesCache(sessionId, next);
}

// ---------------- Frame normalization (handles many shapes) ----------------
function normalizeFrameItem(f, idx) {
  // Allow strings (url)
  if (typeof f === "string") {
    const u = String(f || "").trim();
    return {
      frame_id: "",
      session_doc_path: "",
      image_url: u,
      image_proxy_url: "",
      _key: u ? `url_${idx}` : `idx_${idx}`,
    };
  }

  const obj = f && typeof f === "object" ? f : {};

  // Support nested shapes: {frame:{...}} or {attachment:{...}}
  const frameObj = obj?.frame && typeof obj.frame === "object" ? obj.frame : null;
  const attObj = obj?.attachment && typeof obj.attachment === "object" ? obj.attachment : null;

  const fid = pickFirstId(
    obj?.frame_id,
    obj?.frameId,
    obj?.id,
    frameObj?.frame_id,
    frameObj?.frameId,
    frameObj?.id,
    attObj?.frame_id,
    attObj?.frameId,
    attObj?.id
  );

  const docPath = pickFirstString(
    obj?.session_doc_path,
    obj?.sessionDocPath,
    obj?.frame_doc_path,
    obj?.frameDocPath,
    obj?.doc_path,
    obj?.docPath,
    frameObj?.session_doc_path,
    frameObj?.sessionDocPath,
    frameObj?.frame_doc_path,
    frameObj?.frameDocPath,
    attObj?.session_doc_path,
    attObj?.sessionDocPath
  );

  const signed = pickFirstString(
    obj?.image_url,
    obj?.imageUrl,
    obj?.file_url,
    obj?.fileUrl,
    obj?.url,
    frameObj?.image_url,
    frameObj?.imageUrl,
    frameObj?.file_url,
    frameObj?.fileUrl,
    frameObj?.url,
    attObj?.image_url,
    attObj?.imageUrl,
    attObj?.file_url,
    attObj?.fileUrl,
    attObj?.url
  );

  const proxy = pickFirstString(
    obj?.image_proxy_url,
    obj?.imageProxyUrl,
    obj?.proxy_url,
    obj?.proxyUrl,
    frameObj?.image_proxy_url,
    frameObj?.imageProxyUrl,
    frameObj?.proxy_url,
    frameObj?.proxyUrl,
    attObj?.image_proxy_url,
    attObj?.imageProxyUrl,
    attObj?.proxy_url,
    attObj?.proxyUrl
  );

  const thumb = pickFirstString(
    obj?.thumb_url,
    obj?.thumbUrl,
    obj?.thumbnail_url,
    obj?.thumbnailUrl,
    frameObj?.thumb_url,
    frameObj?.thumbUrl,
    frameObj?.thumbnail_url,
    frameObj?.thumbnailUrl
  );

  const localUrl = pickFirstString(
    obj?.local_url,
    obj?.localUrl,
    obj?.preview_url,
    obj?.previewUrl,
    frameObj?.local_url,
    frameObj?.localUrl
  );

  const localThumb = pickFirstString(
    obj?.local_thumb_url,
    obj?.localThumbUrl,
    obj?.preview_thumb_url,
    obj?.previewThumbUrl,
    frameObj?.local_thumb_url,
    frameObj?.localThumbUrl
  );

  const key = fid || docPath || (signed ? `url_${idx}` : `idx_${idx}`);

  return {
    ...obj,
    frame_id: fid,
    session_doc_path: docPath,
    image_url: signed,
    image_proxy_url: proxy,
    thumb_url: thumb,
    local_url: localUrl,
    local_thumb_url: localThumb,
    _key: key,
  };
}

/**
 * VisionAssistScreenShare
 */
const VisionAssistScreenShare = forwardRef(function VisionAssistScreenShare(
  {
    sessionId,
    assistMode = "B", // "B" | "C"
    viewMode = "split",
    disabled = false,

    // ✅ Parent busy state (preferred prop): when parent is thinking/analyzing, disable capture/upload
    isBusy: isBusyProp,

    // ✅ Backward-compatible prop (older callers)
    parentBusy = false,

    // Optional parent busy-mode label. Pure UI only.
    // Expected values today: "analyzing" | "thinking" | "".
    busyMode = "",

    // Optional: show controls overlay (Analyze / Auto mode)
    showControls = true,

    // Auto-capture tuning (Mode C suggestions)
    probeIntervalMs: probeIntervalMsProp = 750,
    minGapMs: minGapMsProp = 3000,
    forceIntervalMs: forceIntervalMsProp = 12000,
    significantThreshold: significantThresholdProp = 14,
    lowThreshold: lowThresholdProp = 6,
    sustainedCount: sustainedCountProp = 3,

    // ✅ NEW robustness knobs (safe defaults)
    stableMs: stableMsProp = 260, // require “settled” screen before suggesting
    ignoreBorderPct: ignoreBorderPctProp = 0.06, // ignore edges where cursors/toolbars often animate
    ignoreBottomPct: ignoreBottomPctProp = 0.14, // ignore bottom area (chat overlays, sticky bars)
    cursorNoiseFloor: cursorNoiseFloorProp = 1.2, // ignore tiny diffs entirely
    significantHoldMs: significantHoldMsProp = 120, // brief hold for big spikes too (prevents 1-frame glitches)

    // ✅ NEW: if stability never happens (continuous scroll), still allow firing after maxPendingMs
    maxPendingMs: maxPendingMsProp = 1400,

    // Capture tuning
    jpegQuality: jpegQualityProp = 0.85,
    maxCaptureWidth: maxCaptureWidthProp = 1440,

    // Thumbnail tuning
    thumbnailWidth: thumbnailWidthProp = 170,
    thumbnailQuality: thumbnailQualityProp = 0.65,

    // Upload route override
    frameUploadPath,

    // callbacks
    onSharingChange,
    onFrameUploaded,
    onAutoCaptureSuggested,
    onError,
    onDebug,

    // filmstrip inputs
    frames = null,
    showFilmstrip = false,
    selectedFrameId = "",
    onSelectFrame = null,

    // preview rendering
    style,
    videoStyle,
    className,
    showStatus = true,
  },
  ref
) {
  // ✅ single source of truth inside this component:
  //    - if parent passes isBusy, use it
  //    - else fallback to parentBusy for backward compatibility
  const busy = typeof isBusyProp === "boolean" ? isBusyProp : !!parentBusy;

  const [sharing, setSharing] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [displaySurface, setDisplaySurface] = useState("");
  const [lastUploaded, setLastUploaded] = useState(null);

  // ✅ UI-only toggle for auto capture mode (Mode C suggestions)
  const [autoCaptureEnabled, setAutoCaptureEnabled] = useState(true);

  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const inFlightRef = useRef(false);
  const stopRequestedRef = useRef(false);

  const prevTinyRef = useRef(null);
  const lastProbeAtRef = useRef(0);
  const lastSuggestAtRef = useRef(0);
  const lastForcedAtRef = useRef(0);
  const motionStreakRef = useRef(0);

  // ✅ “settle” / stability tracking
  const lastMotionAtRef = useRef(0); // last time we saw movement above noise floor
  const stableStartAtRef = useRef(0); // when we started being “stable”
  const pendingSigRef = useRef(null); // { why, score, extraMeta, firstSeenAt }
  const pendingLowRef = useRef(null); // same, for sustained scroll/motion

  // ✅ NEW: stable baseline + duplicate suppression
  const stableTinyRef = useRef(null); // last stable tiny frame (baseline)
  const lastSuggestedTinySigRef = useRef(""); // last suggestion signature (to suppress duplicates)

  function tinySig(buf) {
    // quick signature: sample across buffer, cheap + stable enough
    try {
      if (!buf || !buf.length) return "";
      const len = buf.length;
      const step = Math.max(4, Math.floor(len / 64)); // ~64 samples
      let h = 2166136261; // FNV-ish
      for (let i = 0; i < len; i += step) {
        h ^= buf[i];
        h = Math.imul(h, 16777619);
      }
      return `s${(h >>> 0).toString(16)}:${len}`;
    } catch {
      return "";
    }
  }

  const assistModeRef = useRef(String(assistMode || "B").toUpperCase());
  useEffect(() => {
    assistModeRef.current = String(assistMode || "B").toUpperCase();
  }, [assistMode]);

  const probeIntervalMs = useMemo(
    () => clamp(Number(probeIntervalMsProp || 750), 250, 5000),
    [probeIntervalMsProp]
  );
  const minGapMs = useMemo(
    () => clamp(Number(minGapMsProp || 3000), 500, 20000),
    [minGapMsProp]
  );
  const forceIntervalMs = useMemo(() => {
    const n = Number(forceIntervalMsProp || 0);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return clamp(n, 2000, 60000);
  }, [forceIntervalMsProp]);

  const significantThreshold = useMemo(
    () => clamp(Number(significantThresholdProp || 14), 4, 60),
    [significantThresholdProp]
  );
  const lowThreshold = useMemo(() => clamp(Number(lowThresholdProp || 6), 1, 40), [lowThresholdProp]);
  const sustainedCount = useMemo(
    () => clamp(Number(sustainedCountProp || 3), 2, 10),
    [sustainedCountProp]
  );

  const stableMs = useMemo(() => clamp(Number(stableMsProp || 260), 120, 900), [stableMsProp]);
  const significantHoldMs = useMemo(
    () => clamp(Number(significantHoldMsProp || 120), 0, 600),
    [significantHoldMsProp]
  );
  const cursorNoiseFloor = useMemo(
    () => clamp(Number(cursorNoiseFloorProp || 1.2), 0.4, 6),
    [cursorNoiseFloorProp]
  );

  // ✅ NEW: max pending window for continuous motion (keeps auto-capture from going silent)
  const maxPendingMs = useMemo(
    () => clamp(Number(maxPendingMsProp || 1400), 300, 6000),
    [maxPendingMsProp]
  );

  const ignoreBorderPct = useMemo(
    () => clamp(Number(ignoreBorderPctProp || 0.06), 0, 0.18),
    [ignoreBorderPctProp]
  );
  const ignoreBottomPct = useMemo(
    () => clamp(Number(ignoreBottomPctProp || 0.14), 0, 0.35),
    [ignoreBottomPctProp]
  );

  const jpegQuality = useMemo(
    () => clamp(Number(jpegQualityProp || 0.85), 0.5, 0.95),
    [jpegQualityProp]
  );
  const maxCaptureWidth = useMemo(
    () => clamp(Number(maxCaptureWidthProp || 1440), 640, 2560),
    [maxCaptureWidthProp]
  );

  const thumbnailWidth = useMemo(
    () => clamp(Number(thumbnailWidthProp || 170), 90, 320),
    [thumbnailWidthProp]
  );
  const thumbnailQuality = useMemo(
    () => clamp(Number(thumbnailQualityProp || 0.65), 0.45, 0.85),
    [thumbnailQualityProp]
  );

  const effectiveFrameUploadPath = useMemo(() => {
    if (frameUploadPath) return frameUploadPath;
    if (!sessionId) return "";
    return `/vision/session/${sessionId}/frame/upload`;
  }, [frameUploadPath, sessionId]);

  const debug = useCallback(
    (msg, obj) => {
      try {
        onDebug?.(msg, obj);
      } catch {}
    },
    [onDebug]
  );

  const emitError = useCallback(
    (err, context = {}) => {
      try {
        onError?.(err, context);
      } catch {}
    },
    [onError]
  );

  const setSharingState = useCallback(
    (val) => {
      setSharing(val);
      try {
        onSharingChange?.(!!val);
      } catch {}
    },
    [onSharingChange]
  );

  const ensureCanvas = useCallback(() => {
    if (canvasRef.current) return canvasRef.current;
    const c = document.createElement("canvas");
    canvasRef.current = c;
    return c;
  }, []);

  const stopTracks = useCallback(() => {
    try {
      const s = streamRef.current;
      if (s) {
        s.getTracks?.().forEach((t) => {
          try {
            t.stop();
          } catch {}
        });
      }
    } catch {}
    streamRef.current = null;

    try {
      const v = videoRef.current;
      if (v) v.srcObject = null;
    } catch {}
  }, []);

  const resetAutoState = useCallback(
    (why = "reset") => {
      prevTinyRef.current = null;
      motionStreakRef.current = 0;
      lastProbeAtRef.current = 0;
      lastSuggestAtRef.current = lastSuggestAtRef.current || 0; // keep gap semantics
      lastForcedAtRef.current = 0;

      lastMotionAtRef.current = 0;
      stableStartAtRef.current = 0;
      pendingSigRef.current = null;
      pendingLowRef.current = null;

      stableTinyRef.current = null;
      lastSuggestedTinySigRef.current = "";
      debug("auto_state_reset", { why });
    },
    [debug]
  );

  const stopSharing = useCallback(
    async (reason = "stop") => {
      stopRequestedRef.current = true;
      setStatusText("");
      setSharingState(false);
      stopTracks();

      resetAutoState(`stopSharing:${reason}`);

      debug("screenshare_stopped", { reason });
    },
    [debug, setSharingState, stopTracks, resetAutoState]
  );

  const startSharing = useCallback(async () => {
    if (disabled) return;
    if (!sessionId) {
      emitError(new Error("Missing sessionId."), { where: "startSharing" });
      return;
    }

    stopRequestedRef.current = false;
    if (streamRef.current && sharing) return;

    try {
      setStatusText("Requesting screen share…");
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 15, max: 30 } },
        audio: false,
      });

      if (stopRequestedRef.current) {
        try {
          stream.getTracks?.().forEach((t) => t.stop());
        } catch {}
        return;
      }

      streamRef.current = stream;

      const info = getTrackInfo(stream);
      setDisplaySurface(info.displaySurface || "");

      const v = videoRef.current;
      if (!v) throw new Error("Video element not ready.");
      v.srcObject = stream;
      v.muted = true;
      v.playsInline = true;

      await new Promise((resolve) => {
        const done = () => resolve(true);
        const t = setTimeout(done, 1200);
        v.onloadedmetadata = () => {
          clearTimeout(t);
          done();
        };
      });

      try {
        await v.play();
      } catch {}

      const track = stream.getVideoTracks?.()?.[0];
      if (track) {
        track.onended = () => stopSharing("track_ended");
      }

      resetAutoState("startSharing");

      setStatusText("");
      setSharingState(true);
      debug("screenshare_started", { track: info });
    } catch (e) {
      setStatusText("");
      setSharingState(false);
      stopTracks();
      emitError(e, { where: "startSharing" });
    }
  }, [
    disabled,
    emitError,
    sessionId,
    sharing,
    setSharingState,
    stopSharing,
    stopTracks,
    debug,
    resetAutoState,
  ]);

  const getVideoDims = useCallback(() => {
    const v = videoRef.current;
    if (!v) return { w: 0, h: 0 };
    return { w: v.videoWidth || 0, h: v.videoHeight || 0 };
  }, []);

  const createThumbnailDataUrl = useCallback(
    (srcCanvas) => {
      try {
        if (!srcCanvas) return "";
        const sw = srcCanvas.width || 0;
        const sh = srcCanvas.height || 0;
        if (!sw || !sh) return "";

        const tw = Math.max(60, Math.round(thumbnailWidth));
        const th = Math.max(30, Math.round((sh / sw) * tw));

        const tcan = document.createElement("canvas");
        tcan.width = tw;
        tcan.height = th;
        const tctx = tcan.getContext("2d", { willReadFrequently: false });
        if (!tctx) return "";

        tctx.drawImage(srcCanvas, 0, 0, tw, th);

        const dataUrl = tcan.toDataURL("image/jpeg", thumbnailQuality);
        if (typeof dataUrl !== "string") return "";
        if (!dataUrl.startsWith("data:image/")) return "";
        if (dataUrl.length > THUMB_MAX_DATAURL_CHARS) return "";
        return dataUrl;
      } catch {
        return "";
      }
    },
    [thumbnailWidth, thumbnailQuality]
  );

  const captureFrameBlob = useCallback(async () => {
    const v = videoRef.current;
    if (!v) throw new Error("Video not ready.");
    const { w, h } = getVideoDims();
    if (!w || !h) throw new Error("Video dimensions not ready.");

    const scale = w > maxCaptureWidth ? maxCaptureWidth / w : 1;
    const outW = Math.max(2, Math.round(w * scale));
    const outH = Math.max(2, Math.round(h * scale));

    const c = ensureCanvas();
    c.width = outW;
    c.height = outH;

    const ctx = c.getContext("2d", { willReadFrequently: false });
    if (!ctx) throw new Error("Canvas context not available.");

    ctx.drawImage(v, 0, 0, outW, outH);

    const thumbnail_data_url = createThumbnailDataUrl(c);

    const blob = await new Promise((resolve) => {
      try {
        c.toBlob((b) => resolve(b), "image/jpeg", jpegQuality);
      } catch {
        resolve(null);
      }
    });

    if (!blob) throw new Error("Failed to create image blob.");
    return {
      blob,
      width: outW,
      height: outH,
      contentType: "image/jpeg",
      thumbnail_data_url,
    };
  }, [ensureCanvas, getVideoDims, jpegQuality, maxCaptureWidth, createThumbnailDataUrl]);

  const uploadFrame = useCallback(
    async ({ blob, width, height, reason = "", meta = {} }) => {
      if (!sessionId) throw new Error("Missing sessionId.");
      if (!effectiveFrameUploadPath) throw new Error("Frame upload path not configured.");
      if (!blob) throw new Error("Missing blob.");

      const ts = nowMs();
      const fileName = `vision_frame_${sessionId}_${ts}.jpg`;

      const fd = new FormData();
      fd.append("file", blob, fileName);

      fd.append("session_id", sessionId);
      fd.append("source", "vision_assist_screenshare");
      fd.append("assist_mode", assistModeRef.current || "B");

      const payloadMeta = {
        session_id: sessionId,
        assist_mode: assistModeRef.current || "B",
        view_mode: viewMode || "",
        reason: reason || "",
        client_frame_at_ms: ts,
        display_surface: displaySurface || "",
        frame_width: width || 0,
        frame_height: height || 0,
        ...((meta && typeof meta === "object") ? meta : {}),
      };
      fd.append("meta", safeJsonStringify(payloadMeta));

      const res = await authedUploadForm(effectiveFrameUploadPath, fd);

      const imageUrl = pickFirstString(
        res?.image_url,
        res?.imageUrl,
        res?.file_url,
        res?.fileUrl,
        res?.signed_url,
        res?.signedUrl
      );

      const frameId = pickFirstId(
        res?.frame_id,
        res?.frameId,
        res?.frame?.id,
        res?.frame?.frame_id,
        res?.id
      );

      const sessionDocPath = pickFirstString(
        res?.session_doc_path,
        res?.sessionDocPath,
        res?.frame_doc_path,
        res?.frameDocPath,
        res?.frame?.session_doc_path
      );

      if (!imageUrl && !frameId && !sessionDocPath) {
        const err = new Error(
          "Upload succeeded but missing image_url and missing frame_id/session_doc_path in response."
        );
        err.response = { status: 200, data: res };
        throw err;
      }

      return {
        ...res,
        image_url: imageUrl || "",
        frame_id: frameId || "",
        session_doc_path: sessionDocPath || "",
        meta: payloadMeta,
        raw: res,
      };
    },
    [sessionId, effectiveFrameUploadPath, viewMode, displaySurface]
  );

  const captureFrame = useCallback(async () => {
    // ✅ Busy means NO capture.
    if (disabled || busy) return null;
    return captureFrameBlob();
  }, [captureFrameBlob, disabled, busy]);

  const captureAndUpload = useCallback(
    async ({ reason = "manual_capture", meta = {} } = {}) => {
      // ✅ Busy means NO upload/analyze and NO capture.
      if (disabled || busy) return null;
      if (!sharing) throw new Error("Not sharing yet.");
      if (inFlightRef.current) return null;

      inFlightRef.current = true;
      try {
        const { blob, width, height, thumbnail_data_url } = await captureFrameBlob();
        const uploaded = await uploadFrame({ blob, width, height, reason, meta });

        const frameId = String(uploaded?.frame_id || "").trim();
        const docPath = String(uploaded?.session_doc_path || "").trim();
        const imgUrl = String(uploaded?.image_url || "").trim();

        if (thumbnail_data_url) {
          if (frameId) writeThumbDataUrl(sessionId, frameId, thumbnail_data_url);
          if (docPath) writeThumbDataUrl(sessionId, docPath, thumbnail_data_url);
        }

        // ✅ Cache minimal frame index for refresh fallback
        if (sessionId) {
          upsertFramesCache(sessionId, {
            frame_id: frameId,
            session_doc_path: docPath,
          });
        }

        const last = {
          uploaded_at_ms: nowMs(),
          image_url: imgUrl,
          frame_id: frameId,
          session_doc_path: docPath,
          reason: String(reason || ""),
          meta: uploaded?.meta || {},
          thumbnail_data_url: thumbnail_data_url || "",
        };
        setLastUploaded(last);

        try {
          onFrameUploaded?.({ ...uploaded, thumbnail_data_url: thumbnail_data_url || "" });
        } catch {}

        return { ...uploaded, thumbnail_data_url: thumbnail_data_url || "" };
      } finally {
        inFlightRef.current = false;
      }
    },
    [disabled, busy, sharing, captureFrameBlob, uploadFrame, onFrameUploaded, sessionId]
  );

  const captureAndAnalyze = useCallback(
    async ({ reason = "manual_analyze", meta = {} } = {}) => {
      return await captureAndUpload({ reason, meta });
    },
    [captureAndUpload]
  );

  // ---------- Mode C: Auto loop emits SUGGESTIONS ONLY ----------
  const tinyCanvasRef = useRef(null);
  const ensureTinyCanvas = useCallback(() => {
    if (tinyCanvasRef.current) return tinyCanvasRef.current;
    const c = document.createElement("canvas");
    tinyCanvasRef.current = c;
    return c;
  }, []);

  // ✅ If we become busy, reset the diff baseline so we don't “catch up” with a false spike afterward.
  useEffect(() => {
    if (!busy) return;

    // reset baseline + pending, keep lastSuggestAt so minGap stays respected
    prevTinyRef.current = null;
    motionStreakRef.current = 0;
    lastProbeAtRef.current = 0;

    lastMotionAtRef.current = 0;
    stableStartAtRef.current = 0;
    pendingSigRef.current = null;
    pendingLowRef.current = null;

    stableTinyRef.current = null;
    lastSuggestedTinySigRef.current = "";
  }, [busy]);

  const probeFrameTiny = useCallback(() => {
    const v = videoRef.current;
    if (!v) return null;

    const vw = v.videoWidth || 0;
    const vh = v.videoHeight || 0;
    if (!vw || !vh) return null;

    // Keep it small for perf; robustness comes from masking + settle logic
    const targetW = 112;
    const targetH = Math.max(28, Math.round((vh / vw) * targetW));

    const c = ensureTinyCanvas();
    c.width = targetW;
    c.height = targetH;

    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;

    // draw full frame downscaled
    ctx.drawImage(v, 0, 0, targetW, targetH);

    // ✅ mask edge + bottom zones (cursor/toolbars/overlays)
    if (ignoreBorderPct > 0 || ignoreBottomPct > 0) {
      const bx = Math.floor(targetW * ignoreBorderPct);
      const by = Math.floor(targetH * ignoreBorderPct);
      const bottom = Math.floor(targetH * ignoreBottomPct);

      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#000";

      if (bx > 0) {
        ctx.fillRect(0, 0, bx, targetH);
        ctx.fillRect(targetW - bx, 0, bx, targetH);
      }
      if (by > 0) {
        ctx.fillRect(0, 0, targetW, by);
      }
      if (bottom > 0) {
        ctx.fillRect(0, targetH - bottom, targetW, bottom);
      }
      ctx.restore();
    }

    const img = ctx.getImageData(0, 0, targetW, targetH);
    return img?.data || null;
  }, [ensureTinyCanvas, ignoreBorderPct, ignoreBottomPct]);

  const maybeSuggest = useCallback(
    async (why, score, extraMeta = {}) => {
      if (!sessionId) return;
      if (disabled) return;
      if (busy) return; // ✅ Busy means NO suggestions
      if (!sharing) return;
      if (!autoCaptureEnabled) return;

      const t = nowMs();
      const lastAt = lastSuggestAtRef.current || 0;
      if (t - lastAt < minGapMs) return;

      // ✅ Duplicate suppression (signature)
      const sig = String(extraMeta?.tiny_sig || "").trim();
      if (sig) {
        if (sig === lastSuggestedTinySigRef.current) return;
        lastSuggestedTinySigRef.current = sig;
      }

      lastSuggestAtRef.current = t;

      const suggestion = {
        sessionId,
        assistMode: assistModeRef.current || "B",
        viewMode: viewMode || "",
        suggested_at_ms: t,
        why,
        diff_score: score,
        display_surface: displaySurface || "",
        thresholds: {
          significant: significantThreshold,
          low: lowThreshold,
          sustainedCount,
          minGapMs,
          probeIntervalMs,
          forceIntervalMs,
          stableMs,
          maxPendingMs, // ✅ NEW
          ignoreBorderPct,
          ignoreBottomPct,
          cursorNoiseFloor,
          significantHoldMs,
        },
        meta: {
          auto: true,
          why,
          diff_score: score,
          stable_ms: stableMs,
          ...((extraMeta && typeof extraMeta === "object") ? extraMeta : {}),
        },
      };

      try {
        onAutoCaptureSuggested?.(suggestion);
      } catch {}

      debug("mode_c_suggested", suggestion);
    },
    [
      sessionId,
      disabled,
      busy,
      sharing,
      autoCaptureEnabled,
      minGapMs,
      viewMode,
      displaySurface,
      significantThreshold,
      lowThreshold,
      sustainedCount,
      probeIntervalMs,
      forceIntervalMs,
      stableMs,
      maxPendingMs,
      ignoreBorderPct,
      ignoreBottomPct,
      cursorNoiseFloor,
      significantHoldMs,
      onAutoCaptureSuggested,
      debug,
    ]
  );

  useEffect(() => {
    if (!sharing) return;
    if (disabled) return;

    let alive = true;
    let timer = null;

    const clearPending = () => {
      pendingSigRef.current = null;
      pendingLowRef.current = null;
    };

    const maybeFirePendingIfStable = async () => {
      const t = nowMs();

      const stableStart = stableStartAtRef.current || 0;
      const isStableEnough = stableStart && t - stableStart >= stableMs;

      // ✅ Timeout escape hatch: if a pending item is waiting too long, fire anyway
      // (prevents "silent" behavior during continuous scrolling).
      const sigPending = pendingSigRef.current;
      const lowPending = pendingLowRef.current;
      const oldestFirstSeenAt =
        Number(sigPending?.firstSeenAt || 0) || Number(lowPending?.firstSeenAt || 0) || 0;

      const pendingTooLong = oldestFirstSeenAt && t - oldestFirstSeenAt >= maxPendingMs;

      if (!isStableEnough && !pendingTooLong) return false;

      // prefer significant pending first
      const sig = pendingSigRef.current;
      if (sig) {
        // optional hold for significant (prevents 1-frame flashes)
        if (significantHoldMs > 0 && t - (sig.firstSeenAt || t) < significantHoldMs) {
          return false;
        }
        pendingSigRef.current = null;
        pendingLowRef.current = null;

        await maybeSuggest(sig.why, sig.score, {
          ...(sig.extraMeta || {}),
          fired_without_stable: !isStableEnough,
          pending_wait_ms: oldestFirstSeenAt ? t - oldestFirstSeenAt : 0,
        });
        return true;
      }

      const low = pendingLowRef.current;
      if (low) {
        pendingLowRef.current = null;

        await maybeSuggest(low.why, low.score, {
          ...(low.extraMeta || {}),
          fired_without_stable: !isStableEnough,
          pending_wait_ms: oldestFirstSeenAt ? t - oldestFirstSeenAt : 0,
        });
        return true;
      }

      return false;
    };

    const tick = async () => {
      if (!alive) return;

      const mode = assistModeRef.current || "B";

      // ✅ Hard stop for auto behavior if busy:
      if (busy) {
        clearPending();
        timer = setTimeout(tick, Math.max(900, probeIntervalMs * 2));
        return;
      }

      if (!autoCaptureEnabled) {
        clearPending();
        timer = setTimeout(tick, probeIntervalMs);
        return;
      }

      if (mode !== "C") {
        clearPending();
        timer = setTimeout(tick, probeIntervalMs);
        return;
      }

      const t = nowMs();
      if (t - (lastProbeAtRef.current || 0) < probeIntervalMs * 0.8) {
        timer = setTimeout(tick, probeIntervalMs);
        return;
      }
      lastProbeAtRef.current = t;

      // If we already have a pending suggestion, try to fire it once stable OR once pendingTooLong
      if (pendingSigRef.current || pendingLowRef.current) {
        await maybeFirePendingIfStable();
        timer = setTimeout(tick, probeIntervalMs);
        return;
      }

      // ✅ Forced interval: still wait for stability; include a sig if we can
      if (forceIntervalMs > 0) {
        const lastForced = lastForcedAtRef.current || 0;
        if (t - lastForced >= forceIntervalMs) {
          lastForcedAtRef.current = t;
          const tinyNow = probeFrameTiny();
          const sigNow = tinySig(tinyNow);

          pendingSigRef.current = {
            why: "forced_interval",
            score: 0,
            extraMeta: { forced_interval_ms: forceIntervalMs, tiny_sig: sigNow || "" },
            firstSeenAt: t,
          };
          // wait for a “stable window”
          stableStartAtRef.current = 0;
          lastMotionAtRef.current = t;

          timer = setTimeout(tick, probeIntervalMs);
          return;
        }
      }

      const tiny = probeFrameTiny();
      if (!tiny) {
        timer = setTimeout(tick, probeIntervalMs);
        return;
      }

      const prev = prevTinyRef.current;
      prevTinyRef.current = tiny;

      // ✅ Baseline: compare against last stable frame if available, else compare against previous probe
      const baseline = stableTinyRef.current || prev;

      if (!baseline) {
        // establish baseline; assume stable until we see motion
        stableTinyRef.current = tiny;
        stableStartAtRef.current = stableStartAtRef.current || t;
        timer = setTimeout(tick, probeIntervalMs);
        return;
      }

      const score = computeFrameDiffScore(baseline, tiny);
      const sig = tinySig(tiny);

      // ✅ Noise floor: tiny cursor blinks should not count as motion
      if (score <= cursorNoiseFloor) {
        if (!stableStartAtRef.current) stableStartAtRef.current = t;

        // ✅ once we've been stable long enough, lock in a new stable baseline
        if (t - stableStartAtRef.current >= stableMs) {
          stableTinyRef.current = tiny;
        }

        timer = setTimeout(tick, probeIntervalMs);
        return;
      }

      // Any motion above noise resets stability window
      lastMotionAtRef.current = t;
      stableStartAtRef.current = 0;

      // Significant change: record pending but DO NOT fire until stable OR until maxPendingMs timeout
      if (score >= significantThreshold) {
        motionStreakRef.current = 0;
        pendingSigRef.current = {
          why: "significant_change",
          score,
          extraMeta: { tiny_sig: sig },
          firstSeenAt: t,
        };
        timer = setTimeout(tick, probeIntervalMs);
        return;
      }

      // low-threshold motion: build streak; when streak reached, set pending low, then wait for stability OR timeout
      if (score >= lowThreshold) {
        motionStreakRef.current = (motionStreakRef.current || 0) + 1;
      } else {
        motionStreakRef.current = 0;
      }

      if ((motionStreakRef.current || 0) >= sustainedCount) {
        motionStreakRef.current = 0;
        pendingLowRef.current = {
          why: "scroll_or_motion",
          score,
          extraMeta: {
            sustained_count: sustainedCount,
            low_threshold: lowThreshold,
            tiny_sig: sig,
          },
          firstSeenAt: t,
        };
        timer = setTimeout(tick, probeIntervalMs);
        return;
      }

      timer = setTimeout(tick, probeIntervalMs);
    };

    timer = setTimeout(tick, probeIntervalMs);

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [
    sharing,
    disabled,
    busy,
    autoCaptureEnabled,
    probeIntervalMs,
    probeFrameTiny,
    maybeSuggest,
    significantThreshold,
    lowThreshold,
    sustainedCount,
    forceIntervalMs,
    stableMs,
    maxPendingMs, // ✅ NEW
    cursorNoiseFloor,
    significantHoldMs,
  ]);

  useEffect(() => {
    return () => {
      stopTracks();
    };
  }, [stopTracks]);

  // ---------------- Filmstrip URL resolving ----------------
  const [frameUrlMap, setFrameUrlMap] = useState({});
  const frameUrlMapRef = useRef({});
  useEffect(() => {
    frameUrlMapRef.current = frameUrlMap || {};
  }, [frameUrlMap]);

  const inflightResolveRef = useRef(new Set());

  useEffect(() => {
    setFrameUrlMap({});
    frameUrlMapRef.current = {};
    inflightResolveRef.current = new Set();
  }, [sessionId]);

  const setUrlForKeys = useCallback((url, keys = [], { replace = false } = {}) => {
    const u = String(url || "").trim();
    const cleanKeys = (Array.isArray(keys) ? keys : [])
      .map((k) => String(k || "").trim())
      .filter(Boolean);

    if (!u || !cleanKeys.length) return;

    setFrameUrlMap((prev) => {
      const cur = prev && typeof prev === "object" ? prev : {};
      let changed = false;
      const next = { ...cur };

      for (const k of cleanKeys) {
        if (replace || !next[k] || String(next[k]).trim() !== u) {
          next[k] = u;
          changed = true;
        }
      }

      if (changed) {
        frameUrlMapRef.current = next;
        return next;
      }
      return cur;
    });
  }, []);

  const removeKeysIfValueMatches = useCallback((badUrl, keys = []) => {
    const b = String(badUrl || "").trim();
    if (!b) return;

    const cleanKeys = (Array.isArray(keys) ? keys : [])
      .map((k) => String(k || "").trim())
      .filter(Boolean);

    if (!cleanKeys.length) return;

    const current = frameUrlMapRef.current || {};
    const immediate = { ...current };
    let immediateChanged = false;

    for (const k of cleanKeys) {
      if (immediate[k] && String(immediate[k]).trim() === b) {
        delete immediate[k];
        immediateChanged = true;
      }
    }
    if (immediateChanged) frameUrlMapRef.current = immediate;

    setFrameUrlMap((prev) => {
      const cur = prev && typeof prev === "object" ? prev : {};
      let changed = false;
      const next = { ...cur };

      for (const k of cleanKeys) {
        if (next[k] && String(next[k]).trim() === b) {
          delete next[k];
          changed = true;
        }
      }

      if (changed) {
        frameUrlMapRef.current = next;
        return next;
      }
      return cur;
    });
  }, []);

  const resolveFrameUrl = useCallback(
    async (frameId, aliasKey = "", { forceRefresh = false } = {}) => {
      const fid = String(frameId || "").trim();
      const alias = String(aliasKey || "").trim();

      if (!fid) return "";
      if (!sessionId) return "";

      const current = frameUrlMapRef.current || {};
      const already = String(current?.[fid] || "").trim();

      if (!forceRefresh && already) {
        if (alias && !current?.[alias]) setUrlForKeys(already, [alias]);
        return already;
      }

      if (inflightResolveRef.current.has(fid)) return "";
      inflightResolveRef.current.add(fid);

      try {
        const res = await getVisionFrameUrl(sessionId, fid);
        const url = String(
          res?.image_url ||
            res?.imageUrl ||
            res?.image_proxy_url ||
            res?.imageProxyUrl ||
            res?.proxy_url ||
            res?.proxyUrl ||
            res?.url ||
            res?.signed_url ||
            res?.signedUrl ||
            ""
        ).trim();

        if (url) {
          setUrlForKeys(url, [fid, alias].filter(Boolean), { replace: true });
          return url;
        }
      } catch {
        // Parent/session remains authoritative; compatibility resolver is best-effort.
      } finally {
        inflightResolveRef.current.delete(fid);
      }

      return "";
    },
    [sessionId, setUrlForKeys]
  );

  // ✅ Normalize frames + cache them (refresh fallback)
  const normalizedFrames = useMemo(() => {
    const list = Array.isArray(frames) ? frames : [];
    return list.map((f, idx) => normalizeFrameItem(f, idx));
  }, [frames]);

  // ✅ Parent hydration is authoritative.
  // If VisionAssistSession re-signs a historical frame and passes a new URL,
  // overwrite any older child URL immediately. This prevents this component
  // from holding onto a stale signed URL after the parent has already repaired it.
  useEffect(() => {
    const list = Array.isArray(normalizedFrames) ? normalizedFrames : [];
    if (!list.length) return;

    for (const f of list) {
      const fid = String(f?.frame_id || "").trim();
      const docPath = String(f?.session_doc_path || "").trim();
      const key = String(f?._key || fid || docPath || "").trim();

      const parentUrl = pickFirstString(
        f?.local_thumb_url,
        f?.thumb_url,
        f?.local_url,
        f?.image_url,
        f?.image_proxy_url
      );

      if (!parentUrl) continue;
      setUrlForKeys(parentUrl, [key, fid, docPath].filter(Boolean), { replace: true });
    }
  }, [normalizedFrames, setUrlForKeys]);

  useEffect(() => {
    if (!sessionId) return;
    if (!showFilmstrip) return;

    const toCache = normalizedFrames
      .map((f) => ({
        frame_id: String(f?.frame_id || "").trim(),
        session_doc_path: String(f?.session_doc_path || "").trim(),
        saved_at_ms: nowMs(),
      }))
      .filter((x) => x.frame_id || x.session_doc_path);

    if (toCache.length) {
      writeFramesCache(sessionId, toCache);
    }
  }, [sessionId, showFilmstrip, normalizedFrames]);

  const effectiveFramesForFilmstrip = useMemo(() => {
    if (!showFilmstrip) return [];
    if (normalizedFrames && normalizedFrames.length) return normalizedFrames;

    if (!sessionId) return [];
    const cached = readFramesCache(sessionId);
    if (!cached.length) return [];

    return cached.map((x, idx) =>
      normalizeFrameItem(
        {
          frame_id: x.frame_id,
          session_doc_path: x.session_doc_path,
        },
        idx
      )
    );
  }, [showFilmstrip, normalizedFrames, sessionId]);

  useEffect(() => {
    const list = Array.isArray(effectiveFramesForFilmstrip) ? effectiveFramesForFilmstrip : [];
    if (!showFilmstrip) return;
    if (!sessionId) return;
    if (!list.length) return;

    let alive = true;

    (async () => {
      for (const f of list.slice(-30)) {
        if (!alive) return;

        const fid = String(f?.frame_id || "").trim();
        const docPath = String(f?.session_doc_path || "").trim();
        const signed = String(f?.image_url || "").trim();
        const keyPrimary = String(f?._key || fid || docPath || "").trim();
        if (!keyPrimary && !fid && !docPath) continue;

        const parentHydrated = pickFirstString(
          f?.local_thumb_url,
          f?.thumb_url,
          f?.local_url,
          signed,
          f?.image_proxy_url
        );

        // Parent/session hydration always wins and may replace an older child URL.
        if (parentHydrated) {
          setUrlForKeys(
            parentHydrated,
            [keyPrimary, fid, docPath].filter(Boolean),
            { replace: true }
          );
          continue;
        }

        // Local data thumbnails do not expire and are safe as a compatibility fallback.
        const cachedThumb =
          (fid ? readThumbDataUrl(sessionId, fid) : "") ||
          (docPath ? readThumbDataUrl(sessionId, docPath) : "");

        if (cachedThumb) {
          setUrlForKeys(cachedThumb, [keyPrimary, fid, docPath].filter(Boolean));
          continue;
        }

        if (fid) {
          await resolveFrameUrl(fid, docPath || keyPrimary || "", { forceRefresh: true });
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [effectiveFramesForFilmstrip, showFilmstrip, sessionId, resolveFrameUrl, setUrlForKeys]);

  // ---------- Imperative API ----------
  useImperativeHandle(
    ref,
    () => ({
      startSharing,
      stopSharing,
      captureFrame,
      captureAndUpload,
      captureAndAnalyze,
      getCachedThumbnail: (frameKey) => readThumbDataUrl(sessionId, frameKey),
      setAutoCaptureEnabled: (v) => setAutoCaptureEnabled(!!v),
      getState: () => ({
        sharing,
        statusText,
        displaySurface,
        assistMode: assistModeRef.current || "B",
        frameUploadPath: effectiveFrameUploadPath || "",
        lastUploaded: lastUploaded || null,
        autoCaptureEnabled: !!autoCaptureEnabled,
        isBusy: !!busy,
        busyMode: String(busyMode || ""),
      }),
      videoEl: () => videoRef.current,
    }),
    [
      startSharing,
      stopSharing,
      captureFrame,
      captureAndUpload,
      captureAndAnalyze,
      sharing,
      statusText,
      displaySurface,
      effectiveFrameUploadPath,
      lastUploaded,
      sessionId,
      autoCaptureEnabled,
      busy,
      busyMode,
    ]
  );

  const hint = useMemo(() => {
    if (!showStatus) return "";
    if (!sharing) return "";
    if (!displaySurface) return "";
    if (String(displaySurface).toLowerCase() === "browser") return "";
    return "For best results, share the specific browser tab or window you want help with.";
  }, [displaySurface, sharing, showStatus]);

  const busyLabel = useMemo(() => {
    if (!busy) return "";
    const mode = String(busyMode || "").trim().toLowerCase();
    if (mode === "analyzing") return "Analyzing shared screen…";
    if (mode === "thinking") return "Lexa is thinking…";
    return "Working…";
  }, [busy, busyMode]);

  const renderFilmstrip = (() => {
    if (!showFilmstrip) return null;
    const list = Array.isArray(effectiveFramesForFilmstrip) ? effectiveFramesForFilmstrip : [];
    if (!list.length) return null;

    const items = list.slice(-12);

    return (
      <div
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: showStatus ? 58 : 12,
          display: "flex",
          gap: 8,
          overflowX: "auto",
          padding: 8,
          borderRadius: 14,
          border: "1px solid rgba(203,213,225,0.92)",
          background: "rgba(255,255,255,0.94)",
          boxShadow: "0 10px 28px rgba(15,23,42,0.12)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {items.map((f, idx) => {
          const fid = String(f?.frame_id || "").trim();
          const docPath = String(f?.session_doc_path || "").trim();
          const key = String(f?._key || fid || docPath || `idx_${idx}`).trim();

          const url = String(frameUrlMap?.[key] || frameUrlMap?.[fid] || frameUrlMap?.[docPath] || "").trim();

          const isSelected =
            !!selectedFrameId &&
            (selectedFrameId === fid || selectedFrameId === docPath || selectedFrameId === key);

          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                try {
                  onSelectFrame?.(f);
                } catch {}
              }}
              style={{
                flex: "0 0 auto",
                width: 76,
                height: 56,
                borderRadius: 10,
                border: isSelected ? "2px solid #1E2A78" : "1px solid #CBD5E1",
                background: "#F8FAFC",
                padding: 0,
                overflow: "hidden",
                cursor: "pointer",
                position: "relative",
                boxShadow: isSelected
                  ? "0 0 0 2px rgba(30,42,120,0.12)"
                  : "0 2px 6px rgba(15,23,42,0.06)",
              }}
              title={fid ? "Saved shared screen" : "Saved shared screen"}
              aria-label={isSelected ? "Selected saved shared screen" : "Saved shared screen"}
            >
              {url ? (
                <img
                  src={url}
                  alt="Saved shared screen"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  onError={() => {
                    removeKeysIfValueMatches(url, [key, fid, docPath].filter(Boolean));
                    if (fid) {
                      resolveFrameUrl(fid, docPath || key || "", {
                        forceRefresh: true,
                      });
                    }
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    background: "#F8FAFC",
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  Screen
                </div>
              )}

              {isSelected ? (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    display: "grid",
                    placeItems: "center",
                    background: "#1E2A78",
                    color: "#FFFFFF",
                    border: "2px solid #FFFFFF",
                    fontSize: 11,
                    fontWeight: 950,
                    lineHeight: 1,
                    boxShadow: "0 2px 6px rgba(15,23,42,0.22)",
                  }}
                >
                  ✓
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  })();

  return (
    <div
      className={className}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        background: "#F8FAFC",
        border: "1px solid #E2E8F0",
        borderRadius: 14,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.65)",
        ...style,
      }}
    >
      <video
        ref={videoRef}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          background: "#FFFFFF",
          ...videoStyle,
        }}
        playsInline
        muted
      />

      {renderFilmstrip}

      {showStatus && (statusText || hint || !sharing || busy) ? (
        <div
          style={{
            position: "absolute",
            left: 12,
            bottom: 12,
            right: 12,
            display: "flex",
            gap: 8,
            alignItems: "center",
            justifyContent: "space-between",
            pointerEvents: "none",
            zIndex: 20,
          }}
        >
          <div
            style={{
              pointerEvents: "none",
              minWidth: 0,
              color: "#334155",
              fontSize: 12,
              fontWeight: 750,
              background: "rgba(255,255,255,0.94)",
              border: "1px solid rgba(203,213,225,0.92)",
              boxShadow: "0 8px 24px rgba(15,23,42,0.10)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              padding: "7px 10px",
              borderRadius: 999,
              display: "inline-flex",
              gap: 8,
              alignItems: "center",
              maxWidth: "72%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={statusText || hint || ""}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                flex: "0 0 auto",
                borderRadius: 999,
                background: sharing ? "#16A34A" : "#94A3B8",
                boxShadow: sharing ? "0 0 0 3px rgba(22,163,74,0.10)" : "none",
              }}
            />

            {!sharing ? (
              <span>Screen not shared yet</span>
            ) : statusText ? (
              <span>{statusText}</span>
            ) : hint ? (
              <span>{hint}</span>
            ) : (
              <span>Shared screen active</span>
            )}
          </div>

          {sharing ? (
            <div
              style={{
                pointerEvents: "none",
                flex: "0 0 auto",
                color: busy ? "#1E2A78" : "#334155",
                fontSize: 11,
                fontWeight: 900,
                background: busy ? "#EEF2FF" : "rgba(255,255,255,0.94)",
                border: busy ? "1px solid #C7D2FE" : "1px solid rgba(203,213,225,0.92)",
                boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
                padding: "7px 10px",
                borderRadius: 999,
                whiteSpace: "nowrap",
              }}
            >
              {busyLabel || "Live shared view"}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});

export default VisionAssistScreenShare;
